// src/services/printerService.js
// ============================================================================
//  SERVICIO DE IMPRESIÓN KOAPrint (frontend estándar)
//  VERSIÓN FINAL FIRMADA POR KEVIN MEJÍA · v2.0.0
//
//  Copiar tal cual en src/services/printerService.js de cada sistema React.
//  Solo revisar las DOS líneas marcadas con "AJUSTAR" (import de api y URL).
// ============================================================================
import api from './api';  // conservado del proyecto

// AJUSTAR: variable de entorno que apunta al backend donde está apps.printer
const PRINTER_API_URL = `${process.env.REACT_APP_FAST_FOOD_SERVICE}/api/hardware`;  // conservado del proyecto

// Paquete de la app Android KOAPrint Móvil (no cambiar)
const KOAPRINT_PACKAGE = 'com.koadevs.koaprint_mobile';

class PrinterService {
  /**
   * Abre la app KOAPrint Móvil en la tablet/celular Android para imprimir.
   *
   * - escposBase64: ticket ESC/POS armado por el backend (negrita, ancho
   *   completo, corte entre venta y comanda). Es el formato preferido.
   * - receiptText: texto plano de respaldo (la app corta donde diga [CUT]).
   * - printerName: nombre de la impresora en la app (si no existe, usa la primera).
   * - auto: true = imprime sin mostrar la ventanita de confirmación.
   *
   * IMPORTANTE: no llamar alert() justo después; en Android cancela el enlace.
   */
  triggerMobilePrint(receiptText, printerName = null, auto = false, escposBase64 = null) {
    if (!receiptText && !escposBase64) {
      console.warn('[KOAPrint] No hay ticket para imprimir.');
      return;
    }

    const params = escposBase64 ? { data: escposBase64 } : { text: receiptText };
    if (printerName) params.printer = printerName;
    if (auto) params.auto = '1';

    const query = new URLSearchParams(params).toString();
    const intentUrl = `intent://print?${query}#Intent;scheme=koaprint;package=${KOAPRINT_PACKAGE};end`;

    try {
      window.location.href = intentUrl;
    } catch (e) {
      console.error('[KOAPrint] No se pudo abrir la app:', e);
    }
  }

  /**
   * Si la impresora es "Inalámbrica" (connection_type = 'rawbt'), el backend no
   * la encola para el agente de PC: la imprime la app móvil con este enlace.
   * Las impresoras USB/Red/Bluetooth las imprime el agente de Windows solo.
   */
  _printOnMobileIfWireless(data, printerName = null, auto = false) {
    if (!data || data.connection_type !== 'rawbt') return;
    this.triggerMobilePrint(data.receipt_text, printerName || data.printer_name, auto, data.escpos_base64);
  }

  /** Comanda de cocina/preparación únicamente. */
  async printCommand(orderData, printerId = null, auto = false) {
    try {
      const response = await api.post(`${PRINTER_API_URL}/print/receipt/`, {
        order: orderData,
        printer_id: printerId,
        only_command: true
      });
      this._printOnMobileIfWireless(response.data, null, auto);
      return response.data;
    } catch (error) {
      console.error('Error al imprimir comanda:', error);
      throw error;
    }
  }

  /** Ticket de venta (+ comanda si la impresora lo tiene configurado o según opciones). */
  async printReceipt(orderData, printerId = null, auto = false, options = {}) {
    try {
      const payload = {
        order: orderData,
        printer_id: printerId
      };
      if (options.onlyReceipt) payload.only_receipt = true;
      if (options.onlyCommand) payload.only_command = true;

      const response = await api.post(`${PRINTER_API_URL}/print/receipt/`, payload);
      this._printOnMobileIfWireless(response.data, null, auto);
      return response.data;
    } catch (error) {
      console.error('Error al imprimir ticket:', error);
      throw error;
    }
  }

  /** Texto libre (reportes, cierres de caja...) en la impresora indicada o la predeterminada. */
  async printCustomTicket(content, documentType = 'other', printerId = null, auto = false) {
    try {
      const payload = {
        content: content,
        document_type: documentType,
        open_cash_drawer: false,
        copies: 1
      };

      if (printerId) {
        payload.printer = printerId;
      } else {
        const printersRes = await this.getPrinters();
        const printers = printersRes.results || printersRes;
        const defaultPrinter = printers.find(p => p.is_default) || printers[0];
        if (defaultPrinter) payload.printer = defaultPrinter.id;
      }

      if (!payload.printer) {
        throw new Error('No hay impresora configurada para imprimir.');
      }

      const response = await api.post(`${PRINTER_API_URL}/print/`, payload);
      this._printOnMobileIfWireless(response.data, null, auto);
      return response.data;
    } catch (error) {
      console.error('Error al imprimir ticket custom:', error);
      throw error;
    }
  }

  async openCashDrawer(printerId = null) {
    try {
      const response = await api.post(`${PRINTER_API_URL}/open-drawer/`, {
        printer_id: printerId
      });
      return response.data;
    } catch (error) {
      console.error('Error al abrir caja:', error);
      throw error;
    }
  }

  async getPrintStatus() {
    try {
      const response = await api.get(`${PRINTER_API_URL}/status/`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener estado:', error);
      return null;
    }
  }

  async getSettings() {
    try {
      const response = await api.get(`${PRINTER_API_URL}/settings/`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener configuración:', error);
      throw error;
    }
  }

  async updateSettings(settingsData) {
    try {
      const response = await api.put(`${PRINTER_API_URL}/settings/`, settingsData);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar configuración:', error);
      throw error;
    }
  }

  async getPrinters() {
    try {
      const response = await api.get(`${PRINTER_API_URL}/printers/`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener impresoras:', error);
      throw error;
    }
  }

  async createPrinter(printerData) {
    try {
      const response = await api.post(`${PRINTER_API_URL}/printers/`, printerData);
      return response.data;
    } catch (error) {
      console.error('Error al crear impresora:', error);
      throw error;
    }
  }

  async updatePrinter(id, printerData) {
    try {
      const response = await api.put(`${PRINTER_API_URL}/printers/${id}/`, printerData);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar impresora:', error);
      throw error;
    }
  }

  async deletePrinter(id) {
    try {
      const response = await api.delete(`${PRINTER_API_URL}/printers/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar impresora:', error);
      throw error;
    }
  }

  /** Impresoras que los agentes de Windows han detectado (para autocompletar). */
  async getAgentPrinters() {
    try {
      const response = await api.get(`${PRINTER_API_URL}/agente/impresoras_detectadas/`);
      return response.data.impresoras || [];
    } catch (error) {
      console.error('Error al obtener impresoras del agente:', error);
      return [];
    }
  }

  async testPrint(id) {
    try {
      const response = await api.post(`${PRINTER_API_URL}/printers/${id}/test_print/`);
      return response.data;
    } catch (error) {
      console.error('Error al probar impresión:', error);
      throw error;
    }
  }
}

export default new PrinterService();
